import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC } from 'react';

import { PATHS } from 'src/common/constants';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { useUserContext } from 'src/common/providers/UserProvider';
import getClassNames from 'src/common/utils/getClassNames';
import AdminNavigationItem from '../NavigationItem';
import styles from './Navigation.module.scss';

type AdminNavigationProps = {
  close?: () => void;
  variant?: 'desktop' | 'mobile';
};

const AdminNavigation: FC<AdminNavigationProps> = ({ close, variant }) => {
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const { validateUserPayoutCurrency } = useCurrencyContext();
  const { user, userRole } = useUserContext();
  const isCaptain = !!user?.is_captain;
  const isOwner = !!user?.owner_approved;
  const isRenter = userRole === 'renter';
  const hasValidPayoutCurrency = validateUserPayoutCurrency();
  const hasPayoutMethod = !!(user?.has_payout_method && hasValidPayoutCurrency);

  const router = useRouter();
  const routeWithTrailingSlash = `${router.pathname}/`;
  const [t] = useTranslation('navbar');

  return (
    <ul className={getClassNames(styles, 'list', variant)}>
      {isNavUpdateEnabled && (isOwner || isRenter) && (
        <AdminNavigationItem
          href={PATHS.ADMIN_PROFILE}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_PROFILE)}
          onClick={close}
          icon="admin"
        >
          {t('admin-profile')}
        </AdminNavigationItem>
      )}

      {!isNavUpdateEnabled && isOwner && (
        <AdminNavigationItem
          href={PATHS.ADMIN_LISTINGS}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_LISTINGS)
            || routeWithTrailingSlash.includes(PATHS.ADMIN_LISTING_EDITOR)}
          onClick={close}
          icon="listings"
        >
          {t('admin-listings')}
        </AdminNavigationItem>
      )}

      {!isNavUpdateEnabled && isOwner && (
        <AdminNavigationItem
          href={PATHS.ADMIN_ADDONS}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_ADDONS)}
          onClick={close}
          icon="basket"
        >
          {t('admin-add-ons')}
        </AdminNavigationItem>
      )}

      {(isOwner || isCaptain) && (
        <AdminNavigationItem
          href={PATHS.ADMIN_PAYMENTS}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_PAYMENTS)}
          showWarning={!hasPayoutMethod}
          onClick={close}
          icon="payment"
        >
          {t('admin-payments')}
        </AdminNavigationItem>
      )}

      {(isOwner || isCaptain) && (
        <AdminNavigationItem
          href={PATHS.ADMIN_TRANSACTIONS}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_TRANSACTIONS)}
          onClick={close}
          icon="bank"
        >
          {t('admin-transactions')}
        </AdminNavigationItem>
      )}

      {isOwner && (
        <AdminNavigationItem
          href={PATHS.ADMIN_CHECKOUT}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_CHECKOUT)}
          onClick={close}
          icon="link"
        >
          {t('admin-checkout')}
        </AdminNavigationItem>
      )}

      {(isOwner || isCaptain) && (
        <AdminNavigationItem
          href={PATHS.ADMIN_SETTINGS}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_SETTINGS)}
          onClick={close}
          icon="gear"
        >
          {t(isNavUpdateEnabled ? 'admin-booking-settings' : 'admin-settings')}
        </AdminNavigationItem>
      )}

      {(isOwner || isCaptain) && (
        <AdminNavigationItem
          href={PATHS.ADMIN_ACCOUNT}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_ACCOUNT)}
          onClick={close}
          icon={isNavUpdateEnabled ? 'house' : 'person'}
        >
          {t(isNavUpdateEnabled ? 'admin-personal-info' : 'admin-account')}
        </AdminNavigationItem>
      )}

      {!isNavUpdateEnabled && (isOwner || isRenter) && (
        <AdminNavigationItem
          href={PATHS.ADMIN_PROFILE}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_PROFILE)}
          onClick={close}
          icon="profile"
        >
          {t('admin-profile')}
        </AdminNavigationItem>
      )}

      {(isRenter) && (
        <AdminNavigationItem
          href={PATHS.ADMIN_SECURITY}
          active={routeWithTrailingSlash.includes(PATHS.ADMIN_SECURITY)}
          onClick={close}
          icon="lock"
        >
          {t('admin-security')}
        </AdminNavigationItem>
      )}
    </ul>
  );
};

export default AdminNavigation;
